import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import { Link } from 'react-router'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'

import { EmptyState, Loader, SectionCard } from '~/shared/ui'
import { formatApiDate } from '~/shared/lib/date'
import type { MemberIds } from '~/entities/person'
import type { PatientUser } from '~/shared/types'
import {
  eventsFromPages,
  useGetPersonTimeline,
  useGetPersonTimelinePages,
  type TimelineEvent,
  type TimelineLane,
} from './MemberTimelineService'
import { useJourneySummary } from './journeySummary'
import JourneySummaryCard from './JourneySummaryCard'
import { useMemberTimelineSummary } from './useMemberTimeline'
import { ALL_LANES, LANES, eventIcon, eventLinkPath } from './timelineConfig'

interface MemberJourneyTimelineProps {
  patient: PatientUser
}

/** Anchor a citation scrolls to. Event ids are unique within a feed, so they can key the DOM. */
const rowElementId = (eventId: string) => `timeline-row-${eventId}`

interface EventGroup {
  heading: string
  events: TimelineEvent[]
}

/** How long a cited row stays marked after the reader follows the citation to it. */
const HIGHLIGHT_MILLIS = 4000

/** How far below the screen the next page starts loading, so the reader rarely waits on it. */
const PAGE_LOOKAHEAD = '600px'

/** Day headings, so a long feed reads as a sequence of days rather than a wall of rows. */
const groupByDay = (events: TimelineEvent[]): EventGroup[] => {
  const groups: EventGroup[] = []
  events.forEach(event => {
    const heading = formatApiDate(event.occurred_at, 'EEEE, MMMM d, yyyy') ?? 'Undated'
    const current = groups[groups.length - 1]
    if (current?.heading === heading) {
      current.events.push(event)
    } else {
      groups.push({ heading, events: [event] })
    }
  })
  return groups
}

/** One event: tone-tinted lane icon, server-composed title/detail, actor, time, optional link. */
const TimelineRow: FC<{ event: TimelineEvent; member: MemberIds; highlighted: boolean }> = ({
  event,
  member,
  highlighted,
}) => {
  const path = eventLinkPath(event, member)
  const time = formatApiDate(event.occurred_at, 'h:mm a')
  // The icon and title now say what kind of event this is, so the lane would only repeat them.
  // A row is a link only when the server named a surface that owns the detail; otherwise it
  // stays a plain row rather than a control that goes nowhere.
  const linkProps = path ? { component: Link, to: path, 'aria-label': event.title } : {}

  return (
    <Box
      {...linkProps}
      sx={theme => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        px: 2,
        py: 1.75,
        color: 'inherit',
        textDecoration: 'none',
        ...(path ? { '&:hover': { bgcolor: theme.palette.tones.neutral.bg } } : {}),
        ...(highlighted ? { bgcolor: theme.palette.tones.info.bg } : {}),
      })}
      id={rowElementId(event.id)}
      data-testid={`timeline-event-${event.id}`}
      data-highlighted={highlighted ? 'true' : undefined}
    >
      <Box
        aria-hidden
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          borderRadius: 1,
          flex: '0 0 auto',
          bgcolor: theme.palette.tones[event.tone].bg,
          color: theme.palette.tones[event.tone].fg,
          '& svg': { fontSize: '2rem' },
        })}
      >
        {eventIcon(event)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: 'text.primary' }}>
          {event.title}
        </Typography>
        {event.detail && (
          <Typography sx={{ mt: 0.25, fontSize: '1.3rem', color: 'text.primary' }}>
            {event.detail}
          </Typography>
        )}
        {event.actor && (
          <Typography sx={{ mt: 0.25, fontSize: '1.25rem', color: 'text.secondary' }}>
            {event.actor}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', pt: 0.25 }}>
        <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {time}
        </Typography>
        {path && (
          <ChevronRightRoundedIcon
            aria-hidden
            sx={{ fontSize: '1.8rem', color: 'text.secondary' }}
          />
        )}
      </Stack>
    </Box>
  )
}

/** Loads the next page as the reader nears the end, or when they ask for it. */
const OlderEvents: FC<{ isFetching: boolean; onLoad: () => void }> = ({ isFetching, onLoad }) => {
  const sentinel = useRef<HTMLDivElement>(null)
  const load = useEffectEvent(onLoad)

  // Observed afresh after every page: an observer reports only changes, so a sentinel still in
  // view once a short page lands would otherwise never ask for the one after it.
  useEffect(() => {
    const element = sentinel.current
    if (isFetching || element == null || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) load()
      },
      { rootMargin: PAGE_LOOKAHEAD },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [isFetching])

  return (
    <Box ref={sentinel} sx={{ display: 'flex', justifyContent: 'center' }}>
      {isFetching ? (
        <Loader inline />
      ) : (
        <Button size="small" onClick={onLoad}>
          Show older events
        </Button>
      )}
    </Box>
  )
}

/**
 * The member's cross-domain journey: milestone events from every domain lane, newest first,
 * grouped by day. Composition, curation, copy, and tone all happen server-side — this renders
 * the feed and maps each event's `link.object_type` to the tab that owns its detail.
 *
 * Lane filtering re-queries rather than filtering in place: `?lanes=` is applied server-side, so
 * a narrowed feed skips the queries for the lanes nobody asked for. The feed arrives a page at a
 * time, and older pages append under the same day headings as the reader scrolls.
 */
const MemberJourneyTimeline: FC<MemberJourneyTimelineProps> = ({ patient }) => {
  const [selectedLanes, setSelectedLanes] = useState<TimelineLane[]>(ALL_LANES)
  const personId = patient.person?.id

  // Nothing selected filters the feed down to nothing. It must not reach the endpoint: a blank
  // `lanes` reads there as "no filter", which would answer with every lane — the opposite of
  // what the chips show.
  const noLanesSelected = selectedLanes.length === 0
  // Every lane selected is the unfiltered feed, so the param drops out and the cache key
  // matches a first, filter-free load.
  const lanes =
    noLanesSelected || selectedLanes.length === ALL_LANES.length
      ? undefined
      : [...selectedLanes].sort().join(',')
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetPersonTimelinePages({ personId: noLanesSelected ? undefined : personId, lanes })
  const events = useMemo(() => eventsFromPages(data?.pages ?? []), [data?.pages])
  const loadOlderEvents = useCallback(() => {
    if (!isFetchingNextPage) void fetchNextPage()
  }, [isFetchingNextPage, fetchNextPage])

  // Read through `noLanesSelected` rather than the query result: disabling a query does not
  // clear its cache, and an empty selection shares the unfiltered key, so `data` here is
  // whatever the last unfiltered fetch stored.
  const groups = useMemo(
    () => (noLanesSelected ? [] : groupByDay(events)),
    [noLanesSelected, events],
  )
  const toggleLane = (lane: TimelineLane) =>
    setSelectedLanes(current =>
      current.includes(lane) ? current.filter(entry => entry !== lane) : [...current, lane],
    )

  const summaryEnabled = useMemberTimelineSummary()
  // The summary describes the member's whole journey, so it reads the whole unpaged feed rather
  // than the list's pages: its citations can name an event older than any page loaded, or in a
  // lane the chips have hidden, and it has to keep saying something when they hide everything.
  const { data: wholeFeed } = useGetPersonTimeline({
    personId: summaryEnabled ? personId : undefined,
  })
  const { summary, isGenerating, hasFailed } = useJourneySummary({
    personId,
    fromFeed: wholeFeed?.summary,
    // Not before the feed answers: until then there is no way to tell a member whose summary
    // is current from one who has none.
    enabled: summaryEnabled && wholeFeed != null,
  })

  // Which row a citation last pointed at. Held here rather than in the card because the rows
  // are rendered here, and the highlight is the whole point of the link.
  const [citedEventId, setCitedEventId] = useState<string | null>(null)
  const clearHighlight = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const showCitedEvent = useCallback((event: TimelineEvent) => {
    // Following a citation into a lane the chips have hidden brings that lane back: the reader
    // asked to be shown this event, and a chip that quietly did nothing would be worse than no
    // chip at all.
    setSelectedLanes(current => (current.includes(event.lane) ? current : [...current, event.lane]))
    setCitedEventId(event.id)
  }, [])

  useEffect(() => () => clearTimeout(clearHighlight.current), [])

  // A cited event older than the pages loaded so far: keep paging until its row arrives.
  const isCitedEventLoaded = citedEventId != null && events.some(event => event.id === citedEventId)
  useEffect(() => {
    if (citedEventId == null || isCitedEventLoaded || !hasNextPage) return
    loadOlderEvents()
  }, [citedEventId, isCitedEventLoaded, hasNextPage, loadOlderEvents])

  // Scrolls once, when the row is on screen: not on each page appended after it, which would
  // pull the reader back while they scroll on.
  useEffect(() => {
    if (citedEventId == null || !isCitedEventLoaded) return
    document.getElementById(rowElementId(citedEventId))?.scrollIntoView({ block: 'center' })
    // The highlight is a pointer, not a selection: it fades so the feed does not keep a row
    // marked long after the reader has looked at it.
    clearTimeout(clearHighlight.current)
    clearHighlight.current = setTimeout(() => setCitedEventId(null), HIGHLIGHT_MILLIS)
  }, [citedEventId, isCitedEventLoaded])

  const lastEngagementOn = data?.pages[0]?.last_engagement_on
  const lastEngagement = lastEngagementOn ? formatApiDate(lastEngagementOn, 'MMM d, yyyy') : null

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }} data-testid="member-journey-timeline">
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 2 }}
      >
        {lastEngagement && (
          <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary', mr: 0.5 }}>
            {`Last engaged ${lastEngagement}`}
          </Typography>
        )}
        {LANES.map(({ lane, label }) => {
          const selected = selectedLanes.includes(lane)
          return (
            <Chip
              key={lane}
              label={label}
              size="small"
              variant={selected ? 'filled' : 'outlined'}
              color={selected ? 'primary' : 'default'}
              onClick={() => toggleLane(lane)}
              aria-pressed={selected}
              data-testid={`timeline-lane-${lane}`}
            />
          )
        })}
        {selectedLanes.length !== ALL_LANES.length && (
          <Button size="small" onClick={() => setSelectedLanes(ALL_LANES)}>
            Show all
          </Button>
        )}
      </Stack>

      {summaryEnabled && (
        <Box sx={{ mb: 2 }}>
          <JourneySummaryCard
            summary={summary}
            isGenerating={isGenerating}
            hasFailed={hasFailed}
            events={wholeFeed?.events ?? []}
            onCitationClick={showCitedEvent}
          />
        </Box>
      )}

      {isLoading ? (
        // `inline` keeps the spinner inside this Box. A bare Loader is absolutely positioned
        // against the nearest positioned ancestor — here the app content pane — so it would
        // cover the whole screen while the filter controls above stayed visible.
        <Loader inline />
      ) : groups.length === 0 ? (
        <SectionCard disablePadding>
          <EmptyState
            icon={<TimelineRoundedIcon />}
            title={noLanesSelected ? 'No lanes selected' : 'No timeline events'}
            caption={
              noLanesSelected
                ? 'Pick at least one lane to see events.'
                : selectedLanes.length === ALL_LANES.length
                  ? 'Nothing has been recorded for this member yet.'
                  : 'No events in the selected lanes. Try widening the filter.'
            }
          />
        </SectionCard>
      ) : (
        <Stack spacing={2}>
          {groups.map(group => (
            <Box key={group.heading}>
              <Typography
                data-testid="timeline-day-heading"
                sx={{ mb: 0.75, fontSize: '1.25rem', fontWeight: 600, color: 'text.secondary' }}
              >
                {group.heading}
              </Typography>
              <SectionCard disablePadding>
                <Box
                  sx={theme => ({
                    '& > * + *': { borderTop: `1px solid ${theme.palette.divider}` },
                  })}
                >
                  {group.events.map(event => (
                    <TimelineRow
                      key={event.id}
                      event={event}
                      member={{ patientId: patient.id, personId }}
                      highlighted={event.id === citedEventId}
                    />
                  ))}
                </Box>
              </SectionCard>
            </Box>
          ))}
          {hasNextPage && <OlderEvents isFetching={isFetchingNextPage} onLoad={loadOlderEvents} />}
        </Stack>
      )}
    </Box>
  )
}

export default MemberJourneyTimeline
